import { useState, useEffect } from 'react';
import { Bars3Icon, UsersIcon, ClipboardDocumentListIcon, PresentationChartLineIcon, ReceiptPercentIcon, CurrencyDollarIcon, BanknotesIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';
import { decryptPayload, formatLabel, formatCurrency, formatMonthYear } from '../../../constants/helpers';
import api from '../../../utils/auth/api';

const Dashboard = ({ setIsMobileOpen }) => {

  const defaultFigures = {
    totalEmployee: 0,
    totalEntries: 0,
    totalServiceFee: 0,
    totalExpense: 0,
    totalServiceFeeAmount: 0,
    totalExpenseAmount: 0,
    totalNetBalance: 0,
    currentMonth: null,
    currentYear: null,
    user: {},
  };

  const [loaders, setLoaders] = useState({ fetchStats: false });
  const [greeting, setGreeting] = useState("Hi");
  const [figures, setFigures] = useState(defaultFigures);

  /* ---------------- GREETING ---------------- */
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 17) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, []);

  // Fetch All (Figures/Stats)
  useEffect(() => {
    const fetchData = async () => {
      setLoaders(prev => ({ ...prev, fetchStats: true }));
      try {
        const response = await api.get(`${process.env.REACT_APP_URI}/dashboard/rider-stats`);
        if (response.data.success) {
          const decryptedData = decryptPayload(response.data.encryptedData);
          setFigures(decryptedData);
        } else {
          setFigures(defaultFigures);
          toast.error(response.data.message);
        }
      } catch (err) {
        setFigures(defaultFigures);
        console.error('Error fetching Rider Dashboard Data:', err);
        toast.error(err?.response?.data.message || "Network Error getting Rider Dashboard Data!")
      } finally {
        setLoaders(prev => ({ ...prev, fetchStats: false }));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Skeleton Loader
  const SkeletonCard = ({ height = 20, width = "full", rounded = "2xl" }) => (
    <div className={`animate-pulse bg-gray-200 rounded-${rounded} w-${width} h-${height}`} ></div>
  );

  return (
    <div className="w-full pb-10">
      {/* Header */}
      <div className="w-full mb-5 flex flex-wrap items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <button
            className="md:hidden p-2 bg-white shadow rounded-lg"
            onClick={() => setIsMobileOpen(true)}
          >
            <Bars3Icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
          </button>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide">
            <span className="text-blue-600">Dash</span>
            <span className="text-gray-800">board</span>
          </h1>
        </div>
      </div>

      <hr className="border-t border-gray-200 my-4" />

      {/* Stats */}
      {loaders.fetchStats ? (
        <div className="space-y-8 mt-10">
          <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-gray-100 p-3 sm:p-6">
            <div className="flex flex-col gap-2">
              <SkeletonCard height={6} width="3/4" rounded="xl" />
              <SkeletonCard height={4} width="1/2" rounded="xl" />
              <SkeletonCard height={4} width="2/3" rounded="xl" />
              <SkeletonCard height={4} width="1/3" rounded="xl" />
            </div>
          </div>
          <div className="bg-white rounded-3xl shadow-md p-3 sm:p-6 border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <SkeletonCard height={6} width="1/4" rounded="xl" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((_, idx) => (
                <div key={idx} className="relative p-6 rounded-3xl bg-gray-300 shadow-lg">
                  <SkeletonCard height={6} width="3/4" rounded="xl" />
                  <div className="mt-4">
                    <SkeletonCard height={10} width="full" rounded="xl" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-3xl shadow-md p-3 sm:p-6 border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <SkeletonCard height={6} width="1/4" rounded="xl" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[1, 2, 3].map((_, idx) => (
                <div
                  key={idx}
                  className="bg-gray-100 rounded-2xl p-5 text-center"
                >
                  <SkeletonCard height={4} width="1/3" rounded="xl" />
                  <div className="flex justify-center mt-3">
                    <SkeletonCard height={8} width="2/3" rounded="xl" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-gray-100 p-3 sm:p-6">
            {!figures.user ? (
              <div className="flex flex-col gap-2">
                <h1 className="text-xl sm:text-3xl font-extrabold tracking-tight leading-tight text-gray-800 break-words">
                  {greeting},{" "}
                  <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">
                    Rider
                  </span>
                </h1>
                <p className="text-sm text-gray-500">No data.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <h1 className="text-xl sm:text-3xl font-extrabold tracking-tight leading-tight text-gray-800 break-words">
                  {greeting},{" "}
                  <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">
                    {formatLabel(figures.user?.name) || "Rider"}
                  </span>
                </h1>

                <p className="text-xs sm:text-base text-gray-600 leading-snug">
                  ID · Role:{" "}
                  <span className="font-medium text-gray-800">{figures.user?.userId || "-"} · {formatLabel(figures.user?.role) || "-"}</span>
                </p>

                <p className="text-xs sm:text-base text-gray-600 leading-snug">
                  F/H:{" "}
                  <span className="font-medium text-gray-800">{formatLabel(figures.user?.father_husband_name) || "-"}</span>
                </p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-3xl shadow-md p-3 sm:p-6 border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <PresentationChartLineIcon className="h-6 w-6 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-800">
                Current Month Stats
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  label: "Total Employees",
                  value: figures.totalEmployee,
                  icon: UsersIcon,
                  color: "from-emerald-500 to-emerald-600",
                },
                {
                  label: "Total Entries",
                  value: figures.totalEntries,
                  icon: ClipboardDocumentListIcon,
                  color: "from-amber-500 to-amber-600",
                },
                {
                  label: "Total Service Fee",
                  value: figures.totalServiceFee,
                  icon: CurrencyDollarIcon,
                  color: "from-green-500 to-green-600",
                },
                {
                  label: "Total Expense",
                  value: figures.totalExpense,
                  icon: ReceiptPercentIcon,
                  color: "from-rose-500 to-rose-600",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`relative p-6 rounded-3xl bg-gradient-to-br ${item.color} text-white shadow-lg hover:shadow-2xl transition duration-300`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm opacity-80">{item.label}</p>
                      <h2 className="text-3xl font-bold mt-1">{item.value || 0}</h2>
                    </div>
                    <item.icon className="h-10 w-10 opacity-70" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-md p-3 sm:p-6 border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <BanknotesIcon className="h-6 w-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-800">
                Finance Stats ({formatMonthYear(figures.currentMonth, figures.currentYear)})
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-yellow-50 rounded-2xl p-5 text-center">
                <p className="text-xs text-gray-600">Service Fee</p>
                <p className="text-2xl font-bold text-yellow-600">
                  PKR {formatCurrency(figures.totalServiceFeeAmount) || 0}
                </p>
              </div>

              <div className="bg-red-50 rounded-2xl p-5 text-center">
                <p className="text-xs text-gray-600">Expense</p>
                <p className="text-2xl font-bold text-red-500">
                  PKR {formatCurrency(figures.totalExpenseAmount) || 0}
                </p>
              </div>

              <div className="bg-blue-50 rounded-2xl p-5 text-center">
                <p className="text-xs text-gray-600">Net Balance</p>
                <p className="text-2xl font-bold text-blue-600">
                  PKR {formatCurrency(figures.totalNetBalance) || 0}
                </p>
                <p className="text-[10px] text-gray-400 mt-1">
                  (Service Fee – Expense)
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;